<template>
  <page-wrapper>
    <template #title>
      <div class="h3">Новый объект размещения</div>
      <v-spacer />
      <mobile-floating-bar>
        <div class="d-flex">
          <v-spacer />
          <e-btn variant="secondary" @click="routerBack">Отмена</e-btn>
          <e-btn variant="primary" class="ml-4" :loading="isLoading" @click="save">Добавить</e-btn>
          <confirmation-dialog v-model="isShowConfirmation" :action="submit" confirm-text="Добавить">
            <div class="h3 mb-6">Создание объекта размещения</div>
            <div class="text-secondary">
              После создания объект размещения будет отправлен на рассмотрение.
              <br />
              Дальнейшие исправления данных будут доступны только через Поддержку.
            </div>
          </confirmation-dialog>
        </div>
      </mobile-floating-bar>
    </template>

    <e-form v-model="isValid" ref="formRef" @submit="submit">
      <property-block>
        <template #text>
          <div class="paragraph-l font-600">Общая информация</div>
          <div class="paragraph-s text-secondary mt-2">
            Укажите, пожалуйста, наименование отеля по которому гости могут вас легко найти и идентифицировать
          </div>
        </template>
        <template #content>
          <multilanguage-field
            v-model="data.name"
            :rules="[required()]"
            :common-rules="[maxLength(255)]"
            label="Наименование"
            class="mb-4"
          />
          <e-autocomplete
            v-model="data.type"
            :items="hotelTypeSuggestions"
            :loading="isLoadingHotelTypeSuggestions"
            :rules="[required()]"
            label="Тип объекта размещения"
            item-title="name"
            item-value="cid"
            class="mb-4"
            show-menu-icon
            @update:search="getHotelTypeSuggestions"
          />
          <e-autocomplete
            v-model="data.category"
            :items="hotelCategorySuggestions"
            :loading="isLoadingHotelCategorySuggestions"
            :rules="[required()]"
            label="Категория / звёздность"
            class="mb-4"
            item-title="name"
            item-value="cid"
            show-menu-icon
            @update:search="getHotelCategorySuggestions"
          />
          <city-field v-model="data.city" :rules="[required()]" />
        </template>
      </property-block>
      <divider class="my-6" />
      <property-block>
        <template #text>
          <div class="paragraph-l font-600">Контактные данные</div>
          <div class="paragraph-s text-secondary mt-2">
            Рабочие контактные данные, по которым можно с вами связаться для дальнейшего взаимодействия
          </div>
        </template>
        <template #content>
          <phone-field v-model="data.phone" :rules="[required()]" class="mb-4" />
          <e-input v-model="data.email" :rules="[required(), emailRule()]" label="E-mail" class="mb-4" />
          <address-field v-model="data.address" 
            :rules="[required()]" 
            :common-rules="[maxLength(255)]"
            class="mb-4" label="Адрес" 
          />
          <e-input v-model="data.webSite" :rules="[URLRule()]" label="Адрес сайта (не обязательно)" />
        </template>
      </property-block>
    </e-form>
  </page-wrapper>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue';
import { useRouter } from 'vue-router';
import MobileFloatingBar from '@/core/components/common/MobileFloatingBar.vue';
import AddressField from '@/core/components/fields/AddressField.vue';
import CityField from '@/core/components/fields/CityField.vue';
import PhoneField from '@/core/components/fields/PhoneField.vue';
import Divider from '@/core/components/helpers/Divider.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import PropertyBlock from '@/core/components/interface/PropertyBlock.vue';
import ConfirmationDialog from '@/core/components/modals/ConfirmationDialog.vue';
import { EAutocomplete, EBtn, EForm, EInput, MultilanguageField } from '@/core/components/ui';
import { useSuggestions } from '@/core/composables/suggestions';
import { useRouterBack } from '@/core/composables/useRouterBack';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { getDefaultMultilanguageValues, type MultilanguageValue } from '@/core/utils/languages';
import { emailRule, maxLength, required, URLRule } from '@/core/utils/validation';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

const $router = useRouter();

const { routerBack } = useRouterBack();

const data = ref<{
  name: MultilanguageValue[];
  type: string | null;
  category: null;
  city: string | null;
  phone: string | null;
  email: string | null;
  address: MultilanguageValue[];
  webSite: string | null;
}>({
  name: getDefaultMultilanguageValues(),
  type: null,
  category: null,
  city: null,
  phone: null,
  email: null,
  address: getDefaultMultilanguageValues(),
  webSite: null,
});

const { createObject } = useAccommodationObjectsStore();
const { useHotelTypeSuggestions, useHotelCategorySuggestions } = useSuggestions();

const isValid = ref(false);
const isLoading = ref(false);

const formRef = ref();
const isShowConfirmation = ref(false);

const save = async () => {
  await formRef.value?.validate();

  await nextTick();

  if (!isValid.value) {
    return;
  }

  isShowConfirmation.value = true;
};

const submit = async () => {
  await formRef.value?.validate();

  await nextTick();

  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  const response = await createObject({
    commonInformation: {
      typeCid: data.value.type!,
      name: data.value.name!,
      categoryCid: data.value.category!,
      townCid: data.value.city!,
    },
    commonContact: {
      phone: data.value.phone!,
      email: data.value.email!,
      postalAddress: data.value.address,
      webSite: data.value.webSite || null,
    },
  });

  if (response.data) {
    await $router.push({
      name: RouteNames.ObjectSettings,
      params: {
        id: response.data.addAccommodationObject.accommodationObject?.cid,
        acc_id: 0,
      },
    });
  }

  isLoading.value = false;
};

const {
  isLoading: isLoadingHotelTypeSuggestions,
  suggestions: hotelTypeSuggestions,
  getSuggestions: getHotelTypeSuggestions,
} = useHotelTypeSuggestions();

const {
  isLoading: isLoadingHotelCategorySuggestions,
  suggestions: hotelCategorySuggestions,
  getSuggestions: getHotelCategorySuggestions,
} = useHotelCategorySuggestions();
</script>

<style scoped lang="scss"></style>
